import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles.css';
import { t } from '../i18n/translationStrings.js';

const Header = () => {
  const [searchInput, setSearchInput] = useState('');
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isSearchPage = location.pathname === '/search';

  useEffect(() => {
    if (!localStorage.getItem('lang')) {
      const browserLang = navigator.language.slice(0, 2);
      const allowed = ['en', 'sv', 'fr', 'de', 'es'];
      localStorage.setItem('lang', allowed.includes(browserLang) ? browserLang : 'en');
      setLang(localStorage.getItem('lang'));
    }
  }, []);

  // Close the popup if user clicks outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    }
    if (showLangMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLangMenu]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchInput.trim())}`);
      setSearchInput('');
    }
  };

  const handleLanguagePick = (selected) => {
    localStorage.setItem('lang', selected);
    setLang(selected);
    setShowLangMenu(false);
    window.location.reload();
  };

  return (
    <header>
      <div
        className="header-content container"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {/* Logo Section */}
        <Link to="/" className="logo-link" aria-label="Ebook Gallery Home">
          <img
            src="/images/logo.webp"
            alt="HermesReads Logo, featuring a book with wings"
            className="site-logo"
            width={85}
            height={85}
            style={{ display: 'block' }}
          />
        </Link>

        {/* Navigation Section */}
        <nav aria-label="Main Navigation">
          <ul style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: 0 }}>
            {/* <li><Link to="/shop">{t('navShop')}</Link></li> */}
            <li>
              <Link to="/library">Library</Link>
            </li>
            <li>
              <Link to="/services">Services</Link>
            </li>
            <li>
              <Link to="/publishing">Publishing</Link>
            </li>
            <li>
              <Link to="/categories">{t('navCategories')}</Link>
            </li>

            {/* Language picker */}
            <li style={{ position: 'relative' }}>
              <button
                onClick={() => setShowLangMenu((v) => !v)}
                style={{
                  padding: '0.3rem 0.9rem',
                  borderRadius: '4px',
                  backgroundColor: 'var(--light-gray)',
                  color: 'var(--text-color)',
                  border: '1px solid var(--highlight-color)',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
                aria-haspopup="true"
                aria-expanded={showLangMenu}
              >
                🌐 {t('languageLabel')}
              </button>
              {showLangMenu && (
                <div
                  ref={langMenuRef}
                  style={{
                    position: 'absolute',
                    top: '120%',
                    right: 0,
                    zIndex: 99,
                    background: 'var(--background-color)',
                    border: '1px solid var(--highlight-color)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    minWidth: '140px',
                    padding: '0.5rem 0.2rem',
                  }}
                >
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    <li>
                      <button
                        onClick={() => handleLanguagePick('en')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: lang === 'en' ? 'var(--highlight-color)' : 'var(--text-color)',
                          fontWeight: lang === 'en' ? 'bold' : 'normal',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                        }}
                      >
                        English
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleLanguagePick('sv')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: lang === 'sv' ? 'var(--highlight-color)' : 'var(--text-color)',
                          fontWeight: lang === 'sv' ? 'bold' : 'normal',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                        }}
                      >
                        Svenska
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleLanguagePick('fr')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: lang === 'fr' ? 'var(--highlight-color)' : 'var(--text-color)',
                          fontWeight: lang === 'fr' ? 'bold' : 'normal',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                        }}
                      >
                        Français
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleLanguagePick('de')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: lang === 'de' ? 'var(--highlight-color)' : 'var(--text-color)',
                          fontWeight: lang === 'de' ? 'bold' : 'normal',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                        }}
                      >
                        Deutsch
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleLanguagePick('es')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: lang === 'es' ? 'var(--highlight-color)' : 'var(--text-color)',
                          fontWeight: lang === 'es' ? 'bold' : 'normal',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                        }}
                      >
                        Español
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </li>
          </ul>
        </nav>

        {/* Search Bar Section */}
        <div className={`search mobile-hide-on-search ${isSearchPage ? 'search-hidden' : ''}`}>
          <form onSubmit={handleSearchSubmit}>
            <input
              type="text"
              id="search-input"
              name="query"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
            />
            <button type="submit">{t('searchButton')}</button>
          </form>
        </div>
      </div>
    </header>
  );
};

export default Header;